import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { MonitoringConfigurationInput } from "src/Types/monitoring.types";

interface MonitoringToolbarProps {
  configuration: MonitoringConfigurationInput;
  isDirty: boolean;
  /** Omitted for users who cannot change the monitored city. */
  onChangeLocation?: () => void;
  workspaceSwitcher?: React.ReactNode;
}

const MonitoringToolbar: React.FC<MonitoringToolbarProps> = ({
  configuration,
  isDirty,
  onChangeLocation,
  workspaceSwitcher,
}) => {
  const { t } = useTranslation();
  const addressCount = t("dashboard.toolbar.monitoredAddresses", {
    count: configuration.addresses.length,
  });
  const locationLabel =
    configuration.mode === "COMMUNITY"
      ? configuration.community?.name || t("dashboard.toolbar.community")
      : configuration.addresses.length === 1
        ? configuration.addresses[0].label
        : addressCount;

  return (
    <Box
      component="header"
      sx={{
        height: { xs: 68, sm: 72 },
        px: { xs: 1.5, sm: 2.5 },
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "rgba(11, 15, 23, 0.96)",
        backdropFilter: "blur(18px)",
        zIndex: 20,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} width="100%">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            background: "linear-gradient(135deg, #7c3aed, #6366f1)",
            flexShrink: 0,
          }}
        >
          <LocationOnOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {t("dashboard.toolbar.title")}
        </Typography>
        <Box sx={{ color: "text.secondary", display: { xs: "none", md: "block" } }}>
          ›
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            title={locationLabel}
            noWrap
            sx={{ fontWeight: 700, fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
          >
            {locationLabel}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {configuration.mode === "COMMUNITY"
              ? t("dashboard.toolbar.communityBoundary")
              : addressCount}
          </Typography>
        </Box>
        {workspaceSwitcher}
        {onChangeLocation && (
          <Button
            size="small"
            variant="text"
            startIcon={<EditLocationAltOutlinedIcon />}
            onClick={onChangeLocation}
            sx={{ whiteSpace: "nowrap", px: { xs: 1, sm: 1.5 } }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              {t("dashboard.toolbar.changeLocation")}
            </Box>
            <Box component="span" sx={{ display: { sm: "none" } }}>
              {t("dashboard.toolbar.change")}
            </Box>
          </Button>
        )}
        {onChangeLocation && (
          <Chip
            size="small"
            icon={isDirty ? undefined : <CheckCircleIcon />}
            color={isDirty ? "warning" : "success"}
            label={isDirty ? t("dashboard.toolbar.unsaved") : t("dashboard.setup.savedChip")}
            variant="outlined"
            sx={{ display: { xs: "none", sm: "flex" } }}
          />
        )}
      </Stack>
    </Box>
  );
};

export default MonitoringToolbar;
